import { type FormEvent, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import type { UserPublic } from '@mondy/shared';
import { ApiError } from '@mondy/shared/api-client';
import { api } from '@/api';
import { setToken } from '@/lib/auth';
import { useRefreshCurrentUser } from '@/hooks/useCurrentUser';
import {
  ButtonSpinner,
  FormField,
  inputClasses,
  primaryButtonClasses,
} from '@/components/auth/FormField';

interface ResetResponse {
  user: UserPublic;
  token: string;
}

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const refreshMe = useRefreshCurrentUser();
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setPasswordError(null);

    if (password !== passwordConfirm) {
      setPasswordError('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }

    setPending(true);
    try {
      const res = await api.post<ResetResponse>('/api/auth/reset-password', {
        token,
        password,
        passwordConfirm,
      });
      setToken(res.token);
      await refreshMe();
      navigate('/');
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Password reset failed');
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col px-6 py-8">
      <div className="flex flex-1 flex-col justify-center gap-8">
        <header className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold">Choose a new password</h1>
        </header>

        {!token ? (
          <div className="flex flex-col gap-4">
            <p className="text-sm text-[color:var(--color-fg-muted)]">
              Missing reset token.
            </p>
            <Link
              to="/forgot-password"
              className="text-sm text-[color:var(--color-fg)] underline underline-offset-2"
            >
              Request a new link
            </Link>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <FormField label="New password" htmlFor="password" error={passwordError}>
              <input
                id="password"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                disabled={pending}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputClasses}
              />
            </FormField>
            <FormField label="Confirm new password" htmlFor="passwordConfirm">
              <input
                id="passwordConfirm"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                disabled={pending}
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                className={inputClasses}
              />
            </FormField>
            {error ? (
              <p className="text-sm text-[color:var(--color-danger)]">{error}</p>
            ) : null}
            <button
              type="submit"
              disabled={pending}
              aria-busy={pending}
              className={primaryButtonClasses}
            >
              {pending ? (
                <>
                  <ButtonSpinner />
                  <span>Saving…</span>
                </>
              ) : (
                'Set new password'
              )}
            </button>
            <Link
              to="/forgot-password"
              className="self-start text-sm text-[color:var(--color-fg-muted)] underline underline-offset-2"
            >
              Request a new link
            </Link>
          </form>
        )}
      </div>
    </main>
  );
}
